"use client";
import { useCallback, useEffect, useRef } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import { TagType, TagSummary } from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import { authClient } from "@constructr-sdk/auth/client";
import type {
  UseTagManagementParams,
  UseTagManagementReturn,
} from "~/modules/main/hooks/use-tag-management/use-tag-management.interface";

// Global state atoms
const activeTabAtom = atom<TagType>(TagType.CATEGORY);
const searchQueryAtom = atom<string>("");
const tagsByTypeAtom = atom<Record<TagType, TagSummary[]>>({
  [TagType.CATEGORY]: [],
  [TagType.USE_CASE]: [],
  [TagType.ROLE]: [],
  [TagType.INDUSTRY]: [],
});
const paginationByTypeAtom = atom<
  Record<
    TagType,
    {
      currentPage: number;
      pageSize: number;
      total: number;
    }
  >
>({
  [TagType.CATEGORY]: { currentPage: 1, pageSize: 20, total: 0 },
  [TagType.USE_CASE]: { currentPage: 1, pageSize: 20, total: 0 },
  [TagType.ROLE]: { currentPage: 1, pageSize: 20, total: 0 },
  [TagType.INDUSTRY]: { currentPage: 1, pageSize: 20, total: 0 },
});
const selectedIdsByTypeAtom = atom<Record<TagType, string[]>>({
  [TagType.CATEGORY]: [],
  [TagType.USE_CASE]: [],
  [TagType.ROLE]: [],
  [TagType.INDUSTRY]: [],
});
const duplicateErrorAtom = atom<{ name: string; type: TagType } | null>(null);
const lastSyncedAtAtom = atom<Date | null>(null);

export function useTagManagement(
  params: UseTagManagementParams,
): UseTagManagementReturn {
  // Global state
  const [activeTab, setActiveTabState] = useAtom(activeTabAtom);
  const [searchQuery, setSearchQueryState] = useAtom(searchQueryAtom);
  const [tagsByType, setTagsByType] = useAtom(tagsByTypeAtom);
  const [paginationByType, setPaginationByType] = useAtom(paginationByTypeAtom);
  const [selectedIdsByType, setSelectedIdsByType] = useAtom(
    selectedIdsByTypeAtom,
  );
  const [duplicateError, setDuplicateErrorState] = useAtom(duplicateErrorAtom);
  const [lastSyncedAt, setLastSyncedAt] = useAtom(lastSyncedAtAtom);

  // Organization bootstrap to prevent 401 errors
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: orgs } = authClient.useListOrganizations();
  const didSetActiveRef = useRef<boolean>(false);

  // Bootstrap active organization if not set
  useEffect(() => {
    if (didSetActiveRef.current) return;
    if (!activeOrg?.id && (orgs?.length ?? 0) > 0) {
      didSetActiveRef.current = true;
      void authClient.organization.setActive({ organizationId: orgs![0]!.id });
    }
  }, [activeOrg?.id, orgs]);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Initialize with params if provided
  useEffect(() => {
    if (params.initialActiveTab) {
      setActiveTabState(params.initialActiveTab);
    }
    if (params.initialPageSizeByType) {
      setPaginationByType((prev) => {
        const updated = { ...prev };
        Object.entries(params.initialPageSizeByType ?? {}).forEach(
          ([type, pageSize]) => {
            if (pageSize !== undefined) {
              updated[type as TagType] = {
                ...updated[type as TagType]!,
                pageSize,
              };
            }
          },
        );
        return updated;
      });
    }
  }, [params.initialActiveTab, params.initialPageSizeByType, setActiveTabState, setPaginationByType]);

  // Fetch tags for the active tab with pagination
  const currentPagination = paginationByType[activeTab];
  const {
    data: tagsData,
    isLoading: isLoadingTags,
    refetch: refetchTags,
  } = trpcReactQueryApi.main.tagManagement.byTypeWithPagination.useQuery(
    {
      type: activeTab,
      page: currentPagination?.currentPage ?? 1,
      pageSize: currentPagination?.pageSize ?? 20,
      searchQuery: searchQuery || undefined,
    },
    { enabled: !!activeOrg?.id }
  );

  // Update local state when data is fetched
  useEffect(() => {
    if (tagsData) {
      setTagsByType((prev) => ({
        ...prev,
        [activeTab]: tagsData.tags,
      }));
      setPaginationByType((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab]!,
          total: tagsData.totalCount,
        },
      }));
    }
  }, [tagsData, activeTab, setTagsByType, setPaginationByType]);

  // Create tag mutation
  const createTagMutation =
    trpcReactQueryApi.main.tagManagement.create.useMutation({
      onSuccess: async () => {
        toast.success("Tag created successfully");
        setDuplicateErrorState(null);
        await utils.main.tagManagement.byTypeWithPagination.invalidate();
        await refetchTags();
      },
      onError: (error) => {
        if (error.data?.code === "CONFLICT") {
          toast.error("Duplicate tag name");
        } else {
          toast.error("Failed to create tag");
        }
      },
    });

  // Update tag mutation
  const updateTagMutation =
    trpcReactQueryApi.main.tagManagement.update.useMutation({
      onSuccess: async () => {
        toast.success("Tag updated successfully");
        setDuplicateErrorState(null);
        await utils.main.tagManagement.byTypeWithPagination.invalidate();
        await refetchTags();
      },
      onError: (error) => {
        if (error.data?.code === "CONFLICT") {
          toast.error("Duplicate tag name");
        } else {
          toast.error("Failed to update tag");
        }
      },
    });

  // Delete tag mutation
  const deleteTagMutation =
    trpcReactQueryApi.main.tagManagement.delete.useMutation({
      onSuccess: async () => {
        toast.success("Tag deleted successfully");
        await utils.main.tagManagement.byTypeWithPagination.invalidate();
        await refetchTags();
      },
      onError: () => {
        toast.error("Failed to delete tag");
      },
    });

  // Merge tags mutation
  const mergeTagsMutation =
    trpcReactQueryApi.main.tagManagement.merge.useMutation({
      onSuccess: async () => {
        toast.success("Tags merged successfully");
        // Clear selections for the active type
        setSelectedIdsByType((prev) => ({
          ...prev,
          [activeTab]: [],
        }));
        await utils.main.tagManagement.byTypeWithPagination.invalidate();
        await refetchTags();
      },
      onError: () => {
        toast.error("Failed to merge tags");
      },
    });

  // Export tags query
  const {
    refetch: refetchExport,
    isLoading: isExporting,
  } = trpcReactQueryApi.main.tagManagement.exportByType.useQuery(
    { type: activeTab },
    { enabled: false },
  );

  // Mutations (synchronous UI updates)
  const setActiveTab = useCallback(
    (type: TagType) => {
      setActiveTabState(type);
    },
    [setActiveTabState],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);
      // Reset to page 1 when search changes
      setPaginationByType((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab]!,
          currentPage: 1,
        },
      }));
    },
    [setSearchQueryState, setPaginationByType, activeTab],
  );

  const toggleSelectId = useCallback(
    (tagId: string) => {
      setSelectedIdsByType((prev) => {
        const currentSelections = prev[activeTab] ?? [];
        const isSelected = currentSelections.includes(tagId);
        return {
          ...prev,
          [activeTab]: isSelected
            ? currentSelections.filter((id) => id !== tagId)
            : [...currentSelections, tagId],
        };
      });
    },
    [setSelectedIdsByType, activeTab],
  );

  const setSelectAllOnPage = useCallback(
    (visibleIds: string[], selectAll: boolean) => {
      setSelectedIdsByType((prev) => {
        const currentSelections = prev[activeTab] ?? [];
        if (selectAll) {
          // Add all visible IDs that aren't already selected
          const newSelections = [
            ...currentSelections,
            ...visibleIds.filter((id) => !currentSelections.includes(id)),
          ];
          return {
            ...prev,
            [activeTab]: newSelections,
          };
        } else {
          // Remove all visible IDs from selections
          return {
            ...prev,
            [activeTab]: currentSelections.filter(
              (id) => !visibleIds.includes(id),
            ),
          };
        }
      });
    },
    [setSelectedIdsByType, activeTab],
  );

  const clearSelection = useCallback(() => {
    setSelectedIdsByType((prev) => ({
      ...prev,
      [activeTab]: [],
    }));
  }, [setSelectedIdsByType, activeTab]);

  const setCurrentPage = useCallback(
    (page: number) => {
      setPaginationByType((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab]!,
          currentPage: page,
        },
      }));
    },
    [setPaginationByType, activeTab],
  );

  const setDuplicateError = useCallback(
    (error: { name: string; type: TagType } | null) => {
      setDuplicateErrorState(error);
    },
    [setDuplicateErrorState],
  );

  // Commands (async operations)
  const initialize = useCallback(async () => {
    try {
      await refetchTags();
      setLastSyncedAt(new Date());
    } catch (error) {
      toast.error("Failed to load tags");
    }
  }, [refetchTags, setLastSyncedAt]);

  const createTag = useCallback(
    async (type: TagType, name: string) => {
      try {
        setDuplicateErrorState(null);
        await createTagMutation.mutateAsync({
          type,
          name,
        });
      } catch (error: any) {
        if (error.data?.code === "CONFLICT") {
          setDuplicateErrorState({ name, type });
        }
        throw error;
      }
    },
    [createTagMutation, setDuplicateErrorState],
  );

  const updateTag = useCallback(
    async (id: string, type: TagType, name: string) => {
      try {
        setDuplicateErrorState(null);
        await updateTagMutation.mutateAsync({
          id,
          type,
          name,
        });
      } catch (error: any) {
        if (error.data?.code === "CONFLICT") {
          setDuplicateErrorState({ name, type });
        }
        throw error;
      }
    },
    [updateTagMutation, setDuplicateErrorState],
  );

  const deleteTag = useCallback(
    async (id: string) => {
      await deleteTagMutation.mutateAsync({ id });
    },
    [deleteTagMutation],
  );

  const mergeTags = useCallback(
    async (type: TagType, sourceIds: string[], targetId: string) => {
      if (sourceIds.length === 0) {
        throw new Error("No source tags selected");
      }
      if (sourceIds.includes(targetId)) {
        throw new Error("Target tag cannot be in the source list");
      }
      await mergeTagsMutation.mutateAsync({
        type,
        sourceIds,
        targetId,
      });
    },
    [mergeTagsMutation],
  );

  const exportTags = useCallback(
    async (type: TagType) => {
      try {
        const result = await refetchExport();
        if (result.data) {
          // Create CSV content
          const headers = ["Name", "Type", "Usage Count", "Slug", "Updated At"];
          const rows = result.data.map((tag) => [
            tag.name,
            tag.type,
            tag.usageCount.toString(),
            tag.slug,
            tag.updatedAt?.toISOString() ?? "",
          ]);
          const csvContent = [
            headers.join(","),
            ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
          ].join("\n");

          // Create and trigger download
          const blob = new Blob([csvContent], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `tags-${type}-${new Date().toISOString()}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          toast.success("Tags exported successfully");
        }
      } catch (error) {
        toast.error("Failed to export tags");
      }
    },
    [refetchExport],
  );

  return {
    // State
    activeTab,
    searchQuery,
    tagsByType,
    paginationByType,
    selectedIdsByType,
    duplicateError,
    isLoading: isLoadingTags,
    lastSyncedAt,

    // Mutations
    setActiveTab,
    setSearchQuery,
    toggleSelectId,
    setSelectAllOnPage,
    clearSelection,
    setCurrentPage,
    setDuplicateError,

    // Commands
    initialize,
    createTag,
    updateTag,
    deleteTag,
    mergeTags,
    exportTags,
  };
}